/**
 * 터치 이벤트와 마우스 이벤트를 구분하여 호버 효과를 관리하는 유틸리티
 *
 * 크롬북 등 터치스크린 디바이스에서:
 * - 짧은 터치(탭): 브라우저가 마우스 이벤트를 발생시키므로 mousemove에서 touch-mode를 제거하지 않음
 * - 드래그: 브라우저가 마우스 이벤트를 발생시키지 않으므로 mousemove에서 touch-mode를 제거
 */
interface TouchState {
    startX: number;
    startY: number;
    startTime: number;
    hasMoved: boolean;
}

/**
 * 두 점 사이의 거리 계산
 */
function getDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * 탭인지 드래그인지 판단
 * @param state 터치 시작 상태
 * @param endX 터치 종료 X 좌표
 * @param endY 터치 종료 Y 좌표
 * @param endTime 터치 종료 시간
 * @returns true면 탭, false면 드래그
 */
function isTap(state: TouchState, endX: number, endY: number, endTime: number): boolean {
    const distance = getDistance(state.startX, state.startY, endX, endY);
    const duration = endTime - state.startTime;

    // 10px 이내 이동이고, 300ms 이내이며, 움직임이 없었으면 탭
    return distance < 10 && duration < 300 && !state.hasMoved;
}

/**
 * 터치 이벤트와 마우스 이벤트를 구분하여 터치 모드 관리
 */
export function setupTouchMode(): void {
    let touchState: TouchState | null = null;
    let isRecentTap = false; // 최근에 탭이 발생했는지 여부
    let tapTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * 터치 시작 핸들러
     */
    function handleTouchStart(e: TouchEvent): void {
        const touch = e.touches[0];
        touchState = {
            startX: touch.clientX,
            startY: touch.clientY,
            startTime: Date.now(),
            hasMoved: false,
        };

        // 최근 탭 플래그 초기화
        isRecentTap = false;
        if (tapTimeout) {
            clearTimeout(tapTimeout);
            tapTimeout = null;
        }

        document.body.classList.add('touch-mode');
    }

    /**
     * 터치 이동 핸들러
     */
    function handleTouchMove(): void {
        if (touchState) {
            touchState.hasMoved = true; // 움직임 감지
        }
        document.body.classList.add('touch-mode');
    }

    /**
     * 터치 종료 핸들러
     */
    function handleTouchEnd(e: TouchEvent): void {
        if (touchState) {
            const touch = e.changedTouches[0];
            const wasTap = isTap(touchState, touch.clientX, touch.clientY, Date.now());

            if (wasTap) {
                // 짧은 터치(탭) - 브라우저가 마우스 이벤트를 발생시킬 가능성 높음
                // mousemove에서 touch-mode를 제거하지 않도록 플래그 설정
                isRecentTap = true;

                // 일정 시간 후 플래그 해제 (예: 500ms)
                if (tapTimeout) {
                    clearTimeout(tapTimeout);
                }
                tapTimeout = setTimeout(() => {
                    isRecentTap = false;
                    tapTimeout = null;
                }, 500);
            } else {
                // 드래그 - 브라우저가 마우스 이벤트를 발생시키지 않음
                isRecentTap = false;
            }

            touchState = null;
        }

        document.body.classList.add('touch-mode');
    }

    /**
     * 마우스 이동 핸들러
     * 짧은 터치(탭) 직후에는 touch-mode를 제거하지 않음
     */
    function handleMouseMove(): void {
        // 최근에 탭이 발생했다면 touch-mode를 제거하지 않음
        if (isRecentTap) {
            return;
        }

        // 드래그 후 또는 일반 마우스 사용 시 touch-mode 제거
        document.body.classList.remove('touch-mode');
    }

    // 이벤트 리스너 등록
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
}

